import { ReportRow } from "./types";

export function toNum(val: any): number {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  if (typeof val === "string") return parseFloat(val) || 0;
  // NumericValue from Smithy might be an object with a value or string property
  if (typeof val === "object" && "value" in val)
    return parseFloat(val.value) || 0;
  if (typeof val === "object" && "string" in val)
    return parseFloat(val.string) || 0;
  return Number(val) || 0;
}

/** Sum a numeric field across rows. */
export const sumBy = <T>(rows: T[], f: (r: T) => number): number =>
  rows.reduce((s, r) => s + f(r), 0);

/** Earliest of defined (non-empty) YYYY-MM(-DD) strings; "" if none. */
export const earliest = (vals: string[]): string =>
  vals.filter(Boolean).sort()[0] ?? "";

/** Latest of defined (non-empty) YYYY-MM(-DD) strings; "" if none. */
export const latest = (vals: string[]): string =>
  vals.filter(Boolean).sort().slice(-1)[0] ?? "";

/** The two ending-balance identities, defined once. */
export function computeEndingBalances(r: {
  stIssuance: number;
  ltIssuance: number;
  cumulativeReclass: number;
  invoicesIssued: number;
}): { stEnding: number; ltEnding: number } {
  return {
    stEnding: r.stIssuance + r.cumulativeReclass - r.invoicesIssued,
    ltEnding: r.ltIssuance - r.cumulativeReclass,
  };
}

/**
 * A ReportRow with all neutral defaults; override only the fields you compute.
 * Single source of truth for a blank row's shape (invoiceStatus/trackerTotal/
 * functional-field zeros no longer copy-pasted across builders).
 */
export function createReportRow(overrides: Partial<ReportRow> = {}): ReportRow {
  return {
    fixedFeeId: "",
    companyCode: "",
    resellerId: "",
    region: "",
    dealStartDate: "",
    dealEndDate: "",
    effortType: "",
    contractPrice: 0,
    currencyCode: "",
    fxRate: 1,
    funcCurrency: "",
    stIssuance: 0,
    ltIssuance: 0,
    cumulativeReclass: 0,
    invoicesIssued: 0,
    functionalInvoicesIssued: 0,
    stEnding: 0,
    ltEnding: 0,
    funcStIssuance: 0,
    funcStReclass: 0,
    funcStEnding: 0,
    stFxReval: 0,
    funcLtIssuance: 0,
    funcLtReclass: 0,
    funcLtEnding: 0,
    ltFxReval: 0,
    firstInvoiceBreakdownPeriod: "",
    serviceProvider: "",
    channelCode: "",
    invoiceStatus: "unreconciled",
    trackerTotal: 0,
    ...overrides,
  };
}

export function addMonths(dateStr: string, months: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().split("T")[0];
}

export function getFirstOfMonth(dateStr: string): string {
  return dateStr.substring(0, 7) + "-01";
}

/** Last day of the previous month in YYYY-MM-DD (UTC); the default report date for a month-end close. */
export function getDefaultReportDate(now: Date = new Date()): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0))
    .toISOString()
    .split("T")[0];
}

/** Maps items through an async worker keeping at most `limit` in flight; preserves input order and reports a running completion count. */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
  onProgress?: (completed: number) => void,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  let completed = 0;
  const run = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      results[index] = await worker(items[index], index);
      completed++;
      onProgress?.(completed);
    }
  };
  const poolSize = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: poolSize }, () => run()));
  return results;
}

/** Invoice frequency values from FACD FixedFeeConfig. */
export const INVOICE_FREQUENCY = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  ANNUAL: "ANNUAL",
} as const;

/**
 * Derives the earliest billing period boundary from an invoiceBreakdown map.
 *
 * For MONTHLY configs: uses the map key (invoice issue date) which aligns with
 * the GL posting month stored as billing_period in the FuseInvoiceTracker DDB.
 * This prevents cross-deal contamination when sequential deals share the same PK.
 *
 * For QUARTERLY/ANNUAL configs: uses periodStartDate from the first entry, since
 * the service period starts earlier than the invoice issue date and tracker records
 * for these deals need the wider boundary to capture the full billing period.
 *
 * When frequency is unknown: defaults to map key (precision over recall).
 *
 * Falls back to map key if periodStartDate is absent (regardless of frequency).
 * Returns YYYY-MM format, or empty string if no entries exist.
 */
export function getEarliestInvoicePeriod(
  invoiceBreakdown: Record<string, any>,
  invoiceFrequency?: string,
): string {
  const keys = Object.keys(invoiceBreakdown).sort();
  if (keys.length === 0) return "";

  const needsWiderBoundary =
    invoiceFrequency === INVOICE_FREQUENCY.QUARTERLY ||
    invoiceFrequency === INVOICE_FREQUENCY.ANNUAL;

  if (needsWiderBoundary) {
    const firstEntry = invoiceBreakdown[keys[0]];
    const dateStr = firstEntry?.periodStartDate ?? keys[0];
    return dateStr.substring(0, 7);
  }

  return keys[0].substring(0, 7);
}

export interface ReclassSplit {
  stIssuance: number;
  ltIssuance: number;
  cumulativeReclass: number;
}

/**
 * Computes the ST/LT split and cumulative reclassification for deals without
 * reclassBalance in FACD. Replicates the F3S AdjustmentCalculator.calculateReceivableShiftAmount
 * logic using invoiceBreakdown periodStartDates as the basis for classification.
 *
 * Algorithm (mirrors F3S):
 *   ltAtInception = sum of IB amounts where periodStartDate >= dealStart + 12 months
 *   ltNow         = sum of IB amounts where periodStartDate >= reportDate + 12 months
 *   cumulativeReclass = ltAtInception - ltNow  (total shifted LT -> ST since inception)
 *   stIssuance = contractPrice - ltAtInception
 *
 * Returns null when invoiceBreakdown is empty (no data to compute from).
 *
 * @param contractPrice total deal value
 * @param invoiceBreakdown FACD invoiceBreakdown map (key=invoice date, value has periodStartDate + invoiceAmount)
 * @param dealStartDate deal inception date (YYYY-MM-DD)
 * @param reportDate balance sheet date (YYYY-MM-DD)
 * @returns ST/LT split and cumulative reclassification, or null if no invoiceBreakdown
 */
export function computeReclassForManualDeal(
  contractPrice: number,
  invoiceBreakdown: Record<string, any>,
  dealStartDate: string,
  reportDate: string,
  reclassCutoff: string,
): ReclassSplit | null {
  const ibEntries = Object.values(invoiceBreakdown);
  if (ibEntries.length === 0) return null;

  const inceptionCutoff = addMonths(dealStartDate, 12);
  // Normalize to first-of-month to match F3S adjustmentDate convention (always 1st of month)
  const reportMonthStart = getFirstOfMonth(reportDate);

  const ltAtInception = ibEntries
    .filter((entry) => (entry.periodStartDate ?? "") >= inceptionCutoff)
    .reduce((sum, entry) => sum + toNum(entry.invoiceAmount), 0);

  // No reclassification has fired yet if report is in the deal's start month —
  // F3S fires the first adjustment on the 1st of the month AFTER deal start.
  if (reportMonthStart <= getFirstOfMonth(dealStartDate)) {
    return {
      stIssuance: contractPrice - ltAtInception,
      ltIssuance: ltAtInception,
      cumulativeReclass: 0,
    };
  }

  // LT boundary = reclassCutoff + 12 months, matching F3S's futureInvoicePeriodStartDateCutoff
  // (adjustmentDate + 12). Invoices with periodStartDate >= this are still long-term.
  const longTermBoundary = addMonths(reclassCutoff, 12);

  const ltNow = ibEntries
    .filter((entry) => (entry.periodStartDate ?? "") >= longTermBoundary)
    .reduce((sum, entry) => sum + toNum(entry.invoiceAmount), 0);

  return {
    stIssuance: contractPrice - ltAtInception,
    ltIssuance: ltAtInception,
    cumulativeReclass: ltAtInception - ltNow,
  };
}

export const fmt = (v: number) =>
  v.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export interface FunctionalColumns {
  funcIssuance: number;
  funcReclass: number;
  funcInvoices: number;
  funcEnding: number;
  fxReval: number;
}

/** Populates all precomputed functional currency fields on a ReportRow. */
export function populateFunctionalFields(r: {
  stIssuance: number;
  ltIssuance: number;
  cumulativeReclass: number;
  stEnding: number;
  ltEnding: number;
  fxRate: number;
  invoicesIssued: number;
  functionalInvoicesIssued: number;
  funcStIssuance: number;
  funcStReclass: number;
  funcStEnding: number;
  stFxReval: number;
  funcLtIssuance: number;
  funcLtReclass: number;
  funcLtEnding: number;
  ltFxReval: number;
}): void {
  const fx = r.fxRate;
  const funcInvoices =
    r.functionalInvoicesIssued > 0
      ? r.functionalInvoicesIssued
      : r.invoicesIssued * fx;
  r.functionalInvoicesIssued = funcInvoices;
  // ST
  r.funcStIssuance = r.stIssuance * fx;
  r.funcStReclass = r.cumulativeReclass * fx;
  r.funcStEnding = r.stEnding * fx;
  r.stFxReval =
    r.funcStEnding - (r.funcStIssuance + r.funcStReclass - funcInvoices);
  // LT
  r.funcLtIssuance = r.ltIssuance * fx;
  r.funcLtReclass = r.cumulativeReclass * fx;
  r.funcLtEnding = r.ltEnding * fx;
  r.ltFxReval = r.funcLtEnding - (r.funcLtIssuance - r.funcLtReclass);
}
